<template>
	<li
		class="relative py-2 grid grid-cols-[19%_80%] sm:grid-cols-[10%_30%_20%_20%_20%] sm:gap-0 gap-2 text-gray-700 border-gray-dark border-b"
	>
		<div
			class="sm:px-2.5 sm:row-auto row-start-1 row-end-4 shrink-0 overflow-hidden text-sm bg-white"
		>
			<nuxt-img
				:src="itemImage"
				:alt="itemRef?.Name || ''"
				sizes="120px"
				class="h-[72px] w-full object-contain object-center sm:h-[96px]"
				:provider="itemRef.ImageKey || imageUrl ? undefined : 'storyblok'"
			/>
		</div>

		<div class="sm:px-2.5 px-5 sm:col-auto col-start-2">
			<nuxt-link
				:to="routeToProduct"
				class="mb-2 block sm:font-normal font-bold"
			>
				{{ itemRef.Name }}
			</nuxt-link>
			<p class="hidden sm:block text-[13px]">
				{{ $t("item_number", { value: itemRef.PartNo }) }}
			</p>
			<p v-if="deliveryText" class="text-[13px] text-gray-super-dark mt-1">
				{{ deliveryText }}
			</p>
		</div>

		<div class="sm:px-2.5 px-5 mb-4 sm:col-auto col-start-2">
			<Price
				:old-price="oldPrice"
				:price="itemRef.PriceDisplayIncVat!"
				:additional-text="`/${$t('checkout.per_item')}`"
				show-save-text
			/>
		</div>

		<div class="sm:px-2.5 px-5 sm:col-auto col-start-2">
			<QuantitySelector
				v-model="quantity"
				class="!h-[28px] !w-[108px]"
				:min="1"
				:max="maxQuantity"
				:disabled="getIsLoading"
				is-checkout-view
				@update:modelValue="updateItem()"
			/>
		</div>

		<div
			class="absolute bottom-2 right-4 px-2.5 sm:col-auto col-start-2 sm:static"
		>
			<Price :price="totalPrice" :show-previous="false" />
		</div>

		<XMarkIcon
			class="absolute sm:top-2 sm:bottom-auto sm:right-3 right-0 bottom-2.5 shrink-0 cursor-pointer text-primary h-4 w-4 sm:ml-auto"
			@click="removeItemFromCart"
		/>
	</li>
</template>

<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/solid";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import Price from "../../Product/ProductAttributes/Price.vue";
import QuantitySelector from "../../Product/ProductAttributes/AddToCart/QuantitySelector.vue";

interface Props {
	lineItem: Partial<BasketItem>;
	imageUrl?: string;
}

const { lineItem, imageUrl } = defineProps<Props>();

const config = useRuntimeConfig();
const cartStore = useCartStore();
const { getIsLoading } = storeToRefs(useHelpersStore());
const localePath = useLocalePath();

const ImagesCdnUrl = config.public.norce.cdn;
const itemRef = toRef(() => lineItem);

const productStore = useProductStore();
const { oldPrice } = useProductPrices(itemRef.value);
const { getDeliveryText } = useDeliveryText();

const deliveryText = computed(() => getDeliveryText(itemRef.value, "checkout"));
const itemImage = computed(() => {
	if (imageUrl) {
		return imageUrl;
	} else
		return imageUrl
			? `${ImagesCdnUrl}/${imageUrl}?format=webp&w=120`
			: lineItem?.ImageKey
				? `${ImagesCdnUrl}/${lineItem.ImageKey}?format=webp&w=120`
				: productStore.defaultProductImage;
});
const routeToProduct = computed(() =>
	localePath(`/${itemRef.value?.UniqueName}`)
);

const quantity = ref<number>(itemRef.value?.Quantity ?? 0);
// Stock comes with the basket line itself (`OnHandValue`, mirrored by
// `OnHand.Value`), so the quantity cap needs no network call. This used to
// fetch the whole product per line, on mount — `get-product-by-unique-name`
// is the one product endpoint without a Redis cache, so a five-line cart cost
// five uncached Norce calls every time the checkout rendered. Ported from
// `layers/base/components/Shared/SharedProductOrderItem.vue`.
const maxQuantity = computed(
	() => itemRef.value.OnHandValue ?? itemRef.value.OnHand?.Value ?? 0
);
const updateItem = async () => {
	cartStore?.updateCartItem(
		itemRef.value.LineNo!,
		itemRef.value.PartNo!,
		quantity.value!
	);
};

const totalPrice = computed(() => {
	if (itemRef.value.PriceDisplayIncVat) {
		return itemRef.value.PriceDisplayIncVat * quantity.value;
	}

	return 0;
});

const removeItemFromCart = () => {
	cartStore?.removeFromCart(itemRef.value.LineNo!);
};

watch(itemRef, () => {
	quantity.value = itemRef.value.Quantity ?? 0;
});
</script>
